import React from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import { ConfirmedSvg, InfoSvg } from 'src/assets/index';
import { DATE_FORMAT_DAY_MONTH, Routes } from 'src/constants/index';
import { ICondoReviewBookState } from 'src/store/slices/index';
import { RootState } from 'src/store';
import { BlueButton } from 'src/components/components/index';
import { formatDateCheckInOut } from 'src/utils/index';
import './style.scss';

interface IMapStateToProps {
  condoReviewBookStore: ICondoReviewBookState;
}

interface IProps extends IMapStateToProps {
  isBookingComplete: boolean;
}

export class BookingConfirmedComponent extends React.Component<IProps, null> {
  isPossibleApplicableFees = (): boolean => {
    const { mandatoryFees, fees, housekeepingFees } =
      this.props.condoReviewBookStore.condoBookingSummary.bookingBeforeYouGo;

    return !isEmpty(mandatoryFees) || !isEmpty(fees) || !isEmpty(housekeepingFees);
  };

  isKnowBeforeYouGo = (): boolean => {
    const { urgentInfo, constructionDetails } =
      this.props.condoReviewBookStore.condoBookingSummary.bookingBeforeYouGo;

    return !isEmpty(urgentInfo) || !isEmpty(constructionDetails);
  };

  join = (strings: string[], separator: string): string => {
    return strings.filter((x) => !isUndefined(x) && x.length > 0).join(separator);
  };

  render(): React.ReactNode {
    const { isBookingComplete, condoReviewBookStore } = this.props;
    const { booking, condoBookingSummary } = condoReviewBookStore;
    const { bookingCard, bookingPolicies, bookingBeforeYouGo, mainGuest } = condoBookingSummary;
    const {
      givenName,
      surname,
      email,
      phone,
      addressLine1,
      city,
      stateCode,
      country,
      countryCode,
      isBookingForSomeoneElse,
      additionalEmail,
      phoneCountryCode,
    } = condoReviewBookStore.guest;

    const propertyInfoLine1 = this.join([bookingCard.city, bookingCard.state], ', ');
    const propertyInfoLine2 = this.join(
      [bookingCard.postalCode, bookingCard.postalCodeExtension],
      ' ',
    );

    return (
      isBookingComplete &&
      booking && (
        <div className="condo-booking-confirmed">
          <ConfirmedSvg />
          <p className="condo-booking-confirmed__title-info">
            <FormattedMessage id="thank.you" />, {givenName}!
          </p>
          <p className="condo-booking-confirmed__title">
            <FormattedMessage id="your.booking.is.confirmed" />
          </p>
          <p className="condo-booking-confirmed__address">
            {bookingCard.name}
            <span>
              {' '}
              <FormattedMessage id="is.expecting.you.on" />{' '}
            </span>
            {formatDateCheckInOut(bookingCard.checkIn, DATE_FORMAT_DAY_MONTH)}
          </p>
          <div className="condo-booking-confirmed__number-info-wrapper">
            <p className="condo-booking-confirmed__number-info">
              <FormattedMessage id="confirmation.number" />:{' '}
              {booking ? booking.confirmationNumber : ''}
            </p>
            <p className="condo-booking-confirmed__info-provide">
              <InfoSvg />
              <FormattedMessage id="asked.to.provide.this.number" />
            </p>
            <p className="condo-booking-confirmed__number-info">
              <FormattedMessage id="member.ID" />: {booking ? booking.memberId : ''}
            </p>
            <p className="condo-booking-confirmed__info-provide">
              <InfoSvg />
              <FormattedMessage id="asked.to.provide.this.member.id" />
            </p>
          </div>
          <div className="condo-booking-confirmed__info-wrapper">
            <div className="condo-booking-confirmed__info-block">
              <p className="condo-booking-confirmed__info-title">
                <FormattedMessage id="guest.info" />
              </p>
              <p className="condo-booking-confirmed__info bold">
                {givenName} {surname}
              </p>
              <p className="condo-booking-confirmed__info">
                {isBookingForSomeoneElse ? additionalEmail : email}
              </p>
              <p className="condo-booking-confirmed__info">
                {phoneCountryCode} {phone}
              </p>
              <div className="condo-booking-confirmed__info-address">
                <p className="condo-booking-confirmed__info">{addressLine1}</p>
                <p className="condo-booking-confirmed__info">{city}</p>
                <p className="condo-booking-confirmed__info">
                  {countryCode} {stateCode} {country}
                </p>
              </div>
            </div>
            <div className="condo-booking-confirmed__info-block">
              <p className="condo-booking-confirmed__info-title">
                <FormattedMessage id="property.info" />
              </p>
              <p className="condo-booking-confirmed__info">{booking?.resort?.phones?.join(', ')}</p>
              <p className="condo-booking-confirmed__info">{propertyInfoLine1}</p>
              <p className="condo-booking-confirmed__info">{propertyInfoLine2}</p>
            </div>
          </div>
          {isBookingForSomeoneElse && email ? (
            <div className="condo-booking-confirmed__booked-by-wrapper">
              <div className="condo-booking-confirmed__info-block">
                <p className="condo-booking-confirmed__info-title">
                  <FormattedMessage id="booked.by" />
                </p>
                <p className="condo-booking-confirmed__info">
                  {mainGuest?.givenName ? `${mainGuest?.givenName} ${mainGuest?.surname},` : ''}{' '}
                  {email}
                </p>
              </div>
            </div>
          ) : null}
          <div className="condo-booking-confirmed__cancellation-policy-wrapper">
            {!isUndefined(condoBookingSummary.cancellationPolicyText) &&
              !isEmpty(condoBookingSummary.cancellationPolicyText) ? (
              <div className="condo-booking-confirmed__cancellation-policy-item">
                <p className="condo-booking-confirmed__info-title">
                  <FormattedMessage id="cancellation.policy" />
                </p>
                <p className="condo-booking-confirmed__info">
                  {condoBookingSummary.cancellationPolicyText}
                </p>
              </div>
            ) : null}
            {!isUndefined(bookingPolicies.ageRestriction) &&
              !isEmpty(bookingPolicies.ageRestriction) ? (
              <div className="condo-booking-confirmed__cancellation-policy-item">
                <p className="condo-booking-confirmed__info-title">
                  <FormattedMessage id="age.restriction" />
                </p>
                <p className="condo-booking-confirmed__info">{bookingPolicies.ageRestriction}</p>
              </div>
            ) : null}
            {!isUndefined(bookingPolicies.pets) && !isEmpty(bookingPolicies.pets) ? (
              <div className="condo-booking-confirmed__cancellation-policy-item">
                <p className="condo-booking-confirmed__info-title">
                  <FormattedMessage id="pets" />
                </p>
                <p className="condo-booking-confirmed__info">{bookingPolicies.pets}</p>
              </div>
            ) : null}
            {!isUndefined(bookingPolicies.parking) && !isEmpty(bookingPolicies.parking) ? (
              <div className="condo-booking-confirmed__cancellation-policy-item">
                <p className="condo-booking-confirmed__info-title">
                  <FormattedMessage id="parking" />
                </p>
                {bookingPolicies.parking.map((item, index) => (
                  <p key={index} className="condo-booking-confirmed__info">
                    {item}
                  </p>
                ))}
              </div>
            ) : null}
            {!isUndefined(bookingPolicies.smoking) && !isEmpty(bookingPolicies.smoking) ? (
              <div className="condo-booking-confirmed__cancellation-policy-item">
                <p className="condo-booking-confirmed__info-title">
                  <FormattedMessage id="smoking" />
                </p>
                {bookingPolicies.smoking.map((item, index) => (
                  <p key={index} className="condo-booking-confirmed__info">
                    {item}
                  </p>
                ))}
              </div>
            ) : null}
            <div>
              <p className="condo-booking-confirmed__contact-property">
                <FormattedMessage id="support" />:{' '}
                <span className="condo-booking-confirmed__contact-property-phone">
                  {booking.customerServiceNumber}
                </span>
              </p>
            </div>

            {this.isKnowBeforeYouGo() || this.isPossibleApplicableFees() ? (
              <div className="condo-booking-confirmed__know-wrapper">
                {!isEmpty(bookingBeforeYouGo.urgentInfo) && (
                  <div className="condo-booking-confirmed__know-block">
                    <h4 className="condo-booking-confirmed__know-title">
                      <InfoSvg />
                      <FormattedMessage id="know.before.you.go" />
                    </h4>
                    {!isEmpty(bookingBeforeYouGo.urgentInfo.generalUrgentInfoMessages) && (
                      <div className="condo-booking-confirmed__know-text-wrapper">
                        {bookingBeforeYouGo.urgentInfo.generalUrgentInfoMessages.map(
                          (item, index) => (
                            <p key={index} className="condo-booking-confirmed__know--text">
                              {item}
                            </p>
                          ),
                        )}
                      </div>
                    )}
                    {!isEmpty(
                      bookingBeforeYouGo.urgentInfo.generalUrgentInfoForNonMembersMessages,
                    ) && (
                        <div className="condo-booking-confirmed__know-text-wrapper">
                          {bookingBeforeYouGo.urgentInfo.generalUrgentInfoForNonMembersMessages.map(
                            (item, index) => (
                              <p key={index} className="condo-booking-confirmed__know--text">
                                {item}
                              </p>
                            ),
                          )}
                        </div>
                      )}
                    {bookingBeforeYouGo.urgentInfo.occupancyRestrictions && (
                      <div className="condo-booking-confirmed__know-text-wrapper">
                        <p className="condo-booking-confirmed__know--text">
                          {bookingBeforeYouGo.urgentInfo.occupancyRestrictions}
                        </p>
                      </div>
                    )}
                    {bookingBeforeYouGo.urgentInfo.oneInXRuleApplies && (
                      <div className="condo-booking-confirmed__know-text-wrapper">
                        <p className="condo-booking-confirmed__know--text">
                          {bookingBeforeYouGo.urgentInfo.oneInXRuleApplies}
                        </p>
                      </div>
                    )}
                    {bookingBeforeYouGo.urgentInfo.separateSwimmingPoolForGenders && (
                      <div className="condo-booking-confirmed__know-text-wrapper">
                        <p className="condo-booking-confirmed__know--text">
                          <FormattedMessage id="separate.swimming.pool.for.genders" />
                        </p>
                      </div>
                    )}
                    {bookingBeforeYouGo.urgentInfo.alcoholRestricted && (
                      <div className="condo-booking-confirmed__know-text-wrapper">
                        <p className="condo-booking-confirmed__know--text">
                          <FormattedMessage id="alcohol.restricted" />
                        </p>
                      </div>
                    )}
                    {bookingBeforeYouGo.urgentInfo.alcoholServedOnsite && (
                      <div className="condo-booking-confirmed__know-text-wrapper">
                        <p className="condo-booking-confirmed__know--text">
                          <FormattedMessage id="alcohol.served.onsite" />
                        </p>
                      </div>
                    )}
                    {bookingBeforeYouGo.urgentInfo.resortStayRestrictionsInfo && (
                      <div className="condo-booking-confirmed__know-text-wrapper">
                        <p className="condo-booking-confirmed__know--text">
                          {bookingBeforeYouGo.urgentInfo.resortStayRestrictionsInfo}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                {this.isPossibleApplicableFees() && (
                  <div className="condo-booking-confirmed__know-block">
                    <h4 className="condo-booking-confirmed__know-title">
                      <FormattedMessage id="possible.applicable.fees" />
                    </h4>
                    {!isEmpty(bookingBeforeYouGo.mandatoryFees) && (
                      <div className="condo-booking-confirmed__know-text-wrapper">
                        {bookingBeforeYouGo.mandatoryFees.map((item, index) => (
                          <div key={index} className="condo-booking-confirmed__know-text-block">
                            {item.infoMessage && (
                              <p className="condo-booking-confirmed__know--text">
                                {item.infoMessage}
                              </p>
                            )}
                            {item.additionalInfoMessage && (
                              <p className="condo-booking-confirmed__know--text">
                                {item.additionalInfoMessage}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {!isEmpty(bookingBeforeYouGo.fees) && (
                      <div className="condo-booking-confirmed__know-text-wrapper">
                        {bookingBeforeYouGo.fees.map((item, index) => (
                          <div key={index} className="condo-booking-confirmed__know-text-block">
                            {item.infoMessage && (
                              <p className="condo-booking-confirmed__know--text">
                                {item.infoMessage}
                              </p>
                            )}
                            {item.additionalInfoMessage && (
                              <p className="condo-booking-confirmed__know--text">
                                {item.additionalInfoMessage}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {!isEmpty(bookingBeforeYouGo.housekeepingFees) && (
                      <div className="condo-booking-confirmed__know-text-wrapper">
                        {bookingBeforeYouGo.housekeepingFees.map((item, index) => (
                          <div key={index} className="condo-booking-confirmed__know-text-block">
                            {item.feeSchedule && (
                              <p className="condo-booking-confirmed__know--text">
                                {item.feeSchedule}
                              </p>
                            )}
                            {item.infoMessage && (
                              <p className="condo-booking-confirmed__know--text">
                                {item.infoMessage}
                              </p>
                            )}
                            {item.additionalInfoMessage && (
                              <p className="condo-booking-confirmed__know--text">
                                {item.additionalInfoMessage}
                              </p>
                            )}
                            {item.mandatoryFee && (
                              <p className="condo-booking-confirmed__know--text">
                                <FormattedMessage id="mandatory.fee" />
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {!isEmpty(bookingBeforeYouGo.constructionDetails) && (
                  <div className="condo-booking-confirmed__know-block">
                    <h4 className="condo-booking-confirmed__know-title">
                      <FormattedMessage id="resort.construction.restrictions" />
                    </h4>
                    <div className="condo-booking-confirmed__know-text-wrapper">
                      {bookingBeforeYouGo.constructionDetails.map((item, index) => (
                        <div key={index}>
                          {item.startDate && item.endDate && (
                            <p className="condo-booking-confirmed__know--text">
                              {item.startDate} - {item.endDate}
                            </p>
                          )}
                          {item.infoMessage && (
                            <p className="condo-booking-confirmed__know--text">
                              {item.infoMessage}
                            </p>
                          )}
                          {item.additionalInfoMessage && (
                            <p className="condo-booking-confirmed__know--text">
                              {item.additionalInfoMessage}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>

          <div className="condo-booking-confirmed__btn-wrapper">
            <Link to={`${Routes.CondoSearch}${location.search}`}>
              <BlueButton>
                <FormattedMessage id="find.another.condo" />
              </BlueButton>
            </Link>
          </div>
        </div>
      )
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    condoReviewBookStore: state.condoReviewBookStore,
  };
};

export const BookingConfirmed = connect(mapStateToProps, null)(BookingConfirmedComponent);
